<template>
  <div>
    <Head title="Boeken" />
    <h1 class="mb-8 text-3xl font-bold">{{ bookItems.total }} Boeken</h1>
    <div class="flex items-center justify-between mb-6">
      <search-filter
        v-model="form.search"
        class="mr-4 w-full max-w-md"
        @reset="reset"
      >
        <!-- <label class="block text-gray-700">Municipality:</label>
        <select v-model="form.municipality" class="form-select mt-1 w-full">
          <option :value="null">All</option>
          <option v-for="municipality in municipalities" :value="municipality.id" :key="municipality.id">{{
          municipality.name }}</option>
        </select> -->

        <label class="block text-gray-700 mt-2">Wijk:</label>
        <div class="mt-1 w-full">
          <Multiselect
            v-model="form.municipality"
            :id="'municipality-filter'"
            :searchable="true"
            :label="'name'"
            :valueProp="'id'"
            :options="municipalitiesOptions"
          />
        </div>

        <label class="block text-gray-700 mt-2">Genres:</label>
        <div class="mt-1 w-full">
          <Multiselect
            v-model="form.genre"
            :id="'genres-filter'"
            :mode="'tags'"
            :searchable="true"
            :options="genres"
          />
        </div>

        <label class="block text-gray-700 mt-2">Categorieën:</label>
        <div class="mt-1 w-full">
          <Multiselect
            v-model="form.category"
            :id="'categories-filter'"
            :mode="'tags'"
            :searchable="true"
            :options="categories"
          />
        </div>

        <label class="block text-gray-700 mt-2">Auteurs:</label>
        <div class="mt-1 w-full">
          <Multiselect
            v-model="form.author"
            :id="'authors-filter'"
            :mode="'tags'"
            :searchable="true"
            :options="authors"
          />
        </div>
      </search-filter>
      <Link class="btn-indigo" href="/books/create">
        <span>Aanmaken</span>
        <span class="hidden md:inline">&nbsp;Boek</span>
      </Link>
    </div>

    <ConfirmationModal
      :show="confirmingBookDeletion"
      @close="
        deleteBookForm.book_item_id = null;
        confirmingBookDeletion = false;
      "
    >
      <template #title>Verwijder boek</template>

      <template #content>
        Ben je zeker dat je dit exemplaar wil verwijderen? Alle gelinkte
        conversaties en de uitleengeschiedenis gaan verloren.
      </template>

      <template #footer>
        <SecondaryButton
          @click.native="
            deleteBookForm.book_item_id = null;
            confirmingBookDeletion = false;
          "
        >
          Annuleren
        </SecondaryButton>

        <DangerButton
          class="ml-2"
          @click.native="deleteBook"
          :class="{ 'opacity-25': deleteBookForm.processing }"
          :disabled="deleteBookForm.processing"
        >
          Verwijder exemplaar
        </DangerButton>
      </template>
    </ConfirmationModal>

    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-4 pt-6 px-6 cursor-not-allowed">Cover</th>
          <th
            class="pb-4 pt-6 px-6 cursor-pointer"
            @click="toggleOrder('created_at')"
          >
            Aangemaakt
          </th>
          <th
            class="pb-4 pt-6 px-6 cursor-pointer"
            @click="toggleOrder('serial')"
          >
            Barcode
          </th>
          <th
            class="pb-4 pt-6 px-6 cursor-pointer"
            @click="toggleOrder('book.isbn')"
          >
            ISBN
          </th>
          <th
            class="pb-4 pt-6 px-6 cursor-pointer"
            @click="toggleOrder('book.title')"
          >
            Titel
          </th>
          <th
            class="pb-4 pt-6 px-6 cursor-pointer"
            @click="toggleOrder('finished_loans_count')"
          >
            Gelezen
          </th>
          <th
            class="pb-4 pt-6 px-6 cursor-pointer"
            @click="toggleOrder('activeLoan.user.first_name')"
          >
            Huidige eigenaar
          </th>
          <th class="pb-4 pt-6 px-6"></th>
        </tr>
        <tr
          v-for="bookItem in bookItems.data"
          :key="bookItem.data.id"
          class="hover:bg-gray-100 focus-within:bg-gray-100"
        >
          <td class="border-t">
            <Link
              class="flex items-center px-6 py-4 focus:text-indigo-500"
              :href="`/books/${bookItem.data.book_id}?activeTab=${bookItem.data.serial}`"
            >
              <img
                v-if="bookItem.data.book.data.cover"
                :src="bookItem.data.book.data.cover"
                :alt="bookItem.data.book.data.title"
                class="rounded-full h-8 w-8 object-cover"
              />
            </Link>
          </td>
          <td class="border-t">
            <Link
              class="flex items-center px-6 py-4"
              :href="`/books/${bookItem.data.book_id}?activeTab=${bookItem.data.serial}`"
              tabindex="-1"
            >
              {{ bookItem.data.created_at }}
            </Link>
          </td>
          <td class="border-t flex items-center">
            <button
              type="button"
              class="-mr-1 p-2 rounded-md focus:outline-none"
              aria-label="Verwijder"
              @click.prevent="
                deleteBookForm.book_item_id = bookItem.data.id;
                confirmingBookDeletion = true;
              "
            >
              <svg
                class="h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <Link
              class="flex items-center px-6 py-4"
              :href="`/books/${bookItem.data.book_id}?activeTab=${bookItem.data.serial}`"
              tabindex="-1"
            >
              {{ bookItem.data.serial }}
            </Link>
          </td>
          <td class="border-t">
            <Link
              class="flex items-center px-6 py-4"
              :href="`/books/${bookItem.data.book_id}?activeTab=${bookItem.data.serial}`"
              tabindex="-1"
            >
              {{ bookItem.data.book.data.isbn }}
            </Link>
          </td>
          <td class="border-t">
            <Link
              class="flex items-center px-6 py-4"
              :href="`/books/${bookItem.data.book_id}?activeTab=${bookItem.data.serial}`"
              tabindex="-1"
            >
              <p class="truncate max-w-xs">
                {{ bookItem.data.book.data.title }}
              </p>
            </Link>
          </td>
          <td class="border-t">
            <Link
              class="flex items-center px-6 py-4"
              :href="`/books/${bookItem.data.book_id}?activeTab=${bookItem.data.serial}`"
              tabindex="-1"
            >
              {{ bookItem.data.finished_loans_count || "" }}
            </Link>
          </td>
          <td class="border-t">
            <Link
              class="flex items-center px-4"
              :href="`/books/${bookItem.data.book_id}?activeTab=${bookItem.data.serial}`"
              tabindex="-1"
            >
              <!-- <Link class="flex items-center px-6 py-4" :href="`/users/${bookItem.data.active_loan?.data?.user?.data?.id}`" tabindex="-1"> -->
              {{ bookItem.data.active_loan?.data?.user?.data?.first_name }}
              {{ bookItem.data.active_loan?.data?.user?.data?.last_name }}
            </Link>
          </td>

          <td class="w-px border-t">
            <Link
              class="flex items-center px-4"
              :href="`/books/${bookItem.data.book_id}?activeTab=${bookItem.data.serial}`"
              tabindex="-1"
            >
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </Link>
          </td>
        </tr>
        <tr v-if="bookItems.data.length === 0">
          <td class="px-6 py-4 border-t" colspan="4">Geen boeken gevonden.</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="bookItems.links" />
  </div>
</template>

<script>
import { Head, Link } from "@inertiajs/inertia-vue3";
import Icon from "@/Shared/Icon";
import pickBy from "lodash/pickBy";
import Layout from "@/Shared/Layout";
import throttle from "lodash/throttle";
import mapValues from "lodash/mapValues";
import Pagination from "@/Shared/Pagination";
import SearchFilter from "@/Shared/SearchFilter";
import Multiselect from "@vueform/multiselect";

import ConfirmationModal from "@/Jetstream/ConfirmationModal";
import DangerButton from "@/Jetstream/DangerButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";

export default {
  components: {
    Head,
    Icon,
    Link,
    Pagination,
    SearchFilter,
    Multiselect,
    ConfirmationModal,
    SecondaryButton,
    DangerButton,
  },
  layout: Layout,
  props: {
    filters: Object,
    bookItems: Object,
    municipalities: Array,
    genres: Array,
    categories: Array,
    authors: Array,
    order: String,
    direction: String,
  },

  remember: "form",

  data() {
    return {
      form: {
        search: this.filters.search,
        municipality: this.filters.municipality,
        genre: this.filters.genre || [],
        category: this.filters.category || [],
        author: this.filters.author || [],
        order: this.order,
        direction: this.direction,
      },
      deleteBookForm: {
        book_item_id: null,
      },
      confirmingBookDeletion: false,
    };
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get("/books", pickBy(this.form), { preserveState: true });
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null);
    },
    toggleOrder(field) {
      if (this.form.order != field) {
        this.form.direction = "desc";
      } else {
        this.form.direction = this.form.direction === "asc" ? "desc" : "asc";
      }

      this.form.order = field;
    },
    deleteBook() {
      this.$inertia.delete("/books/" + this.deleteBookForm.book_item_id, {
        preserveState: true,
        onSuccess: () => (this.confirmingBookDeletion = false),
      });
    },
  },
  computed: {
    municipalitiesOptions() {
      return [...[{ name: "All", id: null }], ...this.municipalities];
    },
  },
};
</script>

